import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { EnumCommodity } from '../../../api/Argentia/enums'
import { useHttpError } from '../../../hooks'
import { getActiveBalanceZone, getActiveCompany } from '../../../redux'

import { FlexibilitiesModel } from '../../../api/Argentia/endpoints/flexibilities/model/flexibilities'
import { enumEnumToValueNumber } from '../../../helpers'
import { fetchFlexibilitiesListItems } from './flexibilitiesTable.service'

interface UseFlexibilitiesBaseProps {
  commodity?: EnumCommodity | null
}

interface UseFlexibilitiesGenericProps extends UseFlexibilitiesBaseProps {
  contractCode?: string
  calculationType?: number
  settlementGroupCode?: string
}

interface UseFlexibilitiesByContractProps extends UseFlexibilitiesBaseProps {
  contractCode: string
  calculationType?: never
  settlementGroupCode?: never
}

interface UseFlexibilitiesByCalculationTypeProps extends UseFlexibilitiesBaseProps {
  contractCode?: never
  calculationType: number
  settlementGroupCode?: never
}

interface UseFlexibilitiesBySettlementGroupProps extends UseFlexibilitiesBaseProps {
  contractCode?: never
  calculationType?: never
  settlementGroupCode: string
}

type UseFlexibilitiesProps =
  | UseFlexibilitiesGenericProps
  | UseFlexibilitiesByContractProps
  | UseFlexibilitiesByCalculationTypeProps
  | UseFlexibilitiesBySettlementGroupProps

interface UseFlexibilities {
  flexibilities: FlexibilitiesModel[] | undefined
  isFetchingFlexibilities: boolean
  refreshFlexibilities: () => Promise<void>
}

export default function useFlexibilities({
  commodity,
  contractCode,
  calculationType,
  settlementGroupCode
}: UseFlexibilitiesProps): UseFlexibilities {
  const [t] = useTranslation(['globalerisLibrary', 'comunicationProcedures', 'enums'])
  const [isFetchingFlexibilities, setIsFetchingFlexibilities] = useState<boolean>(false)
  const [flexibilities, setFlexibilities] = useState<FlexibilitiesModel[] | undefined>(undefined)

  const commodityFormat = commodity ? enumEnumToValueNumber(EnumCommodity, commodity) : null
  const activeBalanceZone = getActiveBalanceZone()?.id
  const activeCompany = getActiveCompany()?.id

  const { showError, clearError } = useHttpError()

  const getFlexibilities = async () => {
    if (commodityFormat && (calculationType || contractCode || settlementGroupCode)) {
      await fetchFlexibilitiesList({
        commodityFormat,
        calculationType,
        contractCode,
        settlementGroupCode
      })
    }
  }

  const fetchFlexibilitiesList = async ({
    commodityFormat,
    calculationType,
    settlementGroupCode,
    contractCode
  }: {
    commodityFormat: EnumCommodity
    calculationType?: number
    settlementGroupCode?: string
    contractCode?: string
  }) => {
    setIsFetchingFlexibilities(true)
    setFlexibilities(undefined)

    await fetchFlexibilitiesListItems(
      commodityFormat,
      calculationType,
      settlementGroupCode,
      contractCode
    ).then(response => {
      setIsFetchingFlexibilities(false)

      if (response.success) {
        clearError()
        setFlexibilities(response.data)
      } else {
        response.error &&
          showError({
            title: t('flexibility:title'),
            errorCode: response.error.code,
            onRefresh: async () => {
              fetchFlexibilitiesList({
                commodityFormat,
                calculationType,
                contractCode,
                settlementGroupCode
              })
            }
          })
      }
    })
  }

  // const fetchFlexibilitiesbyClient = async (
  //   clientID: number
  // ): Promise<FlexibilityTableModel[] | undefined> => {
  //   setIsFetchingFlexibilities(true)
  //   setFlexibilities(undefined)

  //   return await fetchFlexibilitiesListItemsByClient(clientID).then(response => {
  //     setIsFetchingFlexibilities(false)
  //     if (response.success) {
  //       clearError()
  //       if (response.data !== undefined) {
  //         return comunicationsFlexibilitiesListItemsMapper(response.data).map(
  //           (item: ProcedureModel) => {
  //             return completeDataTableActions(item)
  //           }
  //         )
  //       } else {
  //         return []
  //       }
  //     } else {
  //       response.error &&
  //         showError({
  //           errorCode: response.error.code,
  //           onRefresh: async () => {
  //             setFlexibilities(await fetchFlexibilitiesbyClient(clientID))
  //           }
  //         })
  //       return []
  //     }
  //   })
  // }

  // const fetchFlexibilitiesbyDates = async (
  //   dates: DatePeriod
  // ): Promise<ProcedureModel[] | undefined> => {
  //   setIsFetchingFlexibilities(true)
  //   setFlexibilities(undefined)

  //   return await fetchFlexibilitiesListItemsByDates(dates).then(response => {
  //     setIsFetchingFlexibilities(false)
  //     if (response.success) {
  //       clearError()
  //       if (response.data !== undefined) {
  //         return comunicationsFlexibilitiesListItemsMapper(response.data).map(
  //           (item: ProcedureModel) => {
  //             return completeDataTableActions(item)
  //           }
  //         )
  //       } else {
  //         return []
  //       }
  //     } else {
  //       response.error &&
  //         showError({
  //           errorCode: response.error.code,
  //           onRefresh: async () => {
  //             setFlexibilities(await fetchFlexibilitiesbyDates(dates))
  //           }
  //         })
  //       return []
  //     }
  //   })
  // }

  const sortedFlexibilities = flexibilities
    ? flexibilities.sort((flexA, flexB) => {
        const subtypeA = flexA.idSubtype ?? 0
        const subtypeB = flexB.idSubtype ?? 0
        return subtypeA - subtypeB || flexB.id - flexA.id
      })
    : flexibilities

  useEffect(() => {
    // onNotificationChange(undefined)

    getFlexibilities()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    commodityFormat,
    calculationType,
    contractCode,
    settlementGroupCode,
    activeBalanceZone,
    activeCompany
  ])

  return {
    flexibilities: sortedFlexibilities,
    isFetchingFlexibilities,
    refreshFlexibilities: getFlexibilities
  }
}
